/*
Test Component for Vehicle Publishing Queue - Phase 1
Simple testing interface to validate queue infrastructure
Author: Assistant
Date: 2025-06-24
*/

import React from 'react'

import { useVehiclePublishQueue } from '../hooks/useVehiclePublishQueue'
import { QueueVehicleItem } from '../interfaces/VehiclePublishQueue'
import { formatDuration } from '../utils/queueUtils'

export const QueueTestComponent: React.FC = () => {
  const { queueState, operations, progress, isProcessing, canProcess } = useVehiclePublishQueue({
    onItemStart: (item) => console.log('Item started:', item.vehicleId),
    onItemComplete: (item, result) => console.log('Item completed:', item.vehicleId, result),
    onItemFailed: (item, error) => console.log('Item failed:', item.vehicleId, error),
    onQueueComplete: (stats) => console.log('Queue completed:', stats),
    onProgress: (progress) => console.log('Progress:', progress + '%'),
  })

  // Mock vehicle data for testing
  const createMockVehicles = (count: number): QueueVehicleItem[] => {
    return Array.from({ length: count }, (_, index) => ({
      vehicleId: `mock-vehicle-${index + 1}`,
      vehicle: {
        _id: `mock-vehicle-${index + 1}`,
        brand: `Brand ${index + 1}`,
        model: `Model ${index + 1}`,
        year: 2020 + index,
        price: 10000 + index * 1000,
      },
      branch: {
        id: `mock-branch-${index + 1}`,
        name: `Branch ${index + 1}`,
      },
      formData: {
        title: `Test Vehicle ${index + 1}`,
        price: 10000 + index * 1000,
        comments: `Test comments for vehicle ${index + 1}`,
      },
    }))
  }

  const handleAddVehicles = (count: number) => {
    const mockVehicles = createMockVehicles(count)
    operations.addItems(mockVehicles)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600'
      case 'processing':
        return 'text-blue-600'
      case 'completed':
        return 'text-green-600'
      case 'failed':
        return 'text-red-600'
      case 'cancelled':
        return 'text-gray-600'
      default:
        return 'text-gray-400'
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Vehicle Publishing Queue - Phase 1 Test</h2>

      {/* Queue Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">Queue Controls</h3>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => handleAddVehicles(1)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add 1 Vehicle
          </button>
          <button
            onClick={() => handleAddVehicles(3)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add 3 Vehicles
          </button>
          <button
            onClick={() => handleAddVehicles(5)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add 5 Vehicles
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={operations.startProcessing}
            disabled={!canProcess}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300"
          >
            Start Processing
          </button>
          <button
            onClick={operations.pauseProcessing}
            disabled={!isProcessing}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300"
          >
            Pause
          </button>
          <button
            onClick={operations.resumeProcessing}
            disabled={queueState.status !== 'paused'}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300"
          >
            Resume
          </button>
          <button
            onClick={operations.cancelProcessing}
            disabled={!isProcessing}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300"
          >
            Cancel
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={operations.retryFailedItems}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Retry Failed
          </button>
          <button onClick={operations.clearQueue} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
            Clear Queue
          </button>
          <button
            onClick={operations.resetQueue}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Reset Queue
          </button>
        </div>
      </div>

      {/* Queue Statistics */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">Queue Statistics</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{queueState.statistics.totalItems}</div>
            <div className="text-sm text-gray-600">Total Items</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{queueState.statistics.completedCount}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{queueState.statistics.failedCount}</div>
            <div className="text-sm text-gray-600">Failed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{queueState.statistics.pendingCount}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-sm text-gray-600 mb-2">Progress: {progress}%</div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Status:</strong>
            <span className={`ml-2 capitalize ${getStatusColor(queueState.status)}`}>{queueState.status}</span>
          </div>
          <div>
            <strong>Processing:</strong> {isProcessing ? 'Yes' : 'No'}
          </div>
          {queueState.statistics.averageProcessingTime && (
            <div>
              <strong>Avg. Time:</strong> {formatDuration(queueState.statistics.averageProcessingTime)}
            </div>
          )}
          {queueState.statistics.estimatedTimeRemaining && (
            <div>
              <strong>Est. Remaining:</strong> {formatDuration(queueState.statistics.estimatedTimeRemaining)}
            </div>
          )}
        </div>
      </div>

      {/* Queue Items */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Queue Items ({queueState.items.length})</h3>

        {queueState.items.length === 0 ? (
          <div className="text-gray-500 text-center py-8">No items in queue. Add some vehicles to get started.</div>
        ) : (
          <div className="space-y-2">
            {queueState.items.map((item) => (
              <div
                key={item.id}
                className={`p-3 rounded border-l-4 ${
                  item.status === 'processing'
                    ? 'border-blue-500 bg-blue-50'
                    : item.status === 'completed'
                      ? 'border-green-500 bg-green-50'
                      : item.status === 'failed'
                        ? 'border-red-500 bg-red-50'
                        : item.status === 'cancelled'
                          ? 'border-gray-500 bg-gray-50'
                          : 'border-yellow-500 bg-yellow-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium">
                      {item.formData?.title || `${item.vehicle.brand} ${item.vehicle.model}`}
                    </div>
                    <div className="text-sm text-gray-600">
                      ID: {item.vehicleId} | Status:
                      <span className={`ml-1 capitalize ${getStatusColor(item.status)}`}>{item.status}</span>
                      {item.retryCount > 0 && (
                        <span className="ml-2 text-orange-600">
                          (Retry {item.retryCount}/{item.maxRetries})
                        </span>
                      )}
                    </div>
                    {item.error && <div className="text-sm text-red-600 mt-1">Error: {item.error}</div>}
                    {item.result && item.result.success && (
                      <div className="text-sm text-green-600 mt-1">✓ {item.result.message}</div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    {item.status === 'failed' && item.retryCount < item.maxRetries && (
                      <button
                        onClick={() => operations.retryItem(item.id)}
                        className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                      >
                        Retry
                      </button>
                    )}
                    <button
                      onClick={() => operations.removeItem(item.id)}
                      disabled={item.status === 'processing'}
                      className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Debug Info */}
      <div className="mt-6 bg-gray-100 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-2">Debug Info</h3>
        <pre className="text-xs overflow-auto">
          {JSON.stringify(
            {
              status: queueState.status,
              currentIndex: queueState.currentIndex,
              itemCount: queueState.items.length,
              canProcess,
              isProcessing,
              progress,
              settings: queueState.settings,
            },
            null,
            2
          )}
        </pre>
      </div>
    </div>
  )
}
